import { useState, useEffect } from "react";
import { useGameAuth } from "@/hooks/useGameAuth";
import { GameLayout } from "@/components/game/GameLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Gift, Users, Check, DollarSign } from "lucide-react";
import cyberBg from "@/assets/game/cyber-expensive.png";

export default function GameInvite() {
  const { session, player, loading, signOut } = useGameAuth();
  const [referralCode, setReferralCode] = useState("");
  const [referrals, setReferrals] = useState<any[]>([]);
  const [copied, setCopied] = useState(false);
  const [totalEarned, setTotalEarned] = useState(0);

  useEffect(() => {
    if (player) {
      fetchReferralData();
    }
  }, [player]);

  const fetchReferralData = async () => {
    if (!player) return;

    // Get player's referral code
    const { data: playerData } = await supabase
      .from("game_players")
      .select("referral_code")
      .eq("id", player.id)
      .single();

    if (playerData?.referral_code) {
      setReferralCode(playerData.referral_code);
    }

    // Get referral history
    const { data: refs } = await supabase
      .from("game_referrals")
      .select("*, referred:referred_player_id(display_name)")
      .eq("referrer_player_id", player.id)
      .order("created_at", { ascending: false });

    if (refs) {
      setReferrals(refs);
      setTotalEarned(refs.filter((r: any) => r.status === "converted").reduce((sum: number, r: any) => sum + Number(r.credit_amount), 0));
    }
  };

  const referralLink = referralCode
    ? `${window.location.origin}/game?ref=${referralCode}`
    : "";

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success("Referral link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!session || !player) return <Navigate to="/game" replace />;

  return (
    <GameLayout player={player} onSignOut={signOut}>
      <div
        className="cyber-page-bg max-w-2xl mx-auto p-4 space-y-4"
        style={{ ['--cyber-bg' as string]: `url(${cyberBg})` }}
      >
        {/* Hero */}
        <div className="text-center py-6">
          <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center">
            <Gift className="w-8 h-8 text-black" />
          </div>
          <h1 className="text-2xl font-extrabold text-game-gradient">Invite Friends</h1>
          <p className="text-gray-400 text-sm mt-1">
            Earn <span className="text-cyan-400 font-bold">$10.00</span> for each friend who joins!
          </p>
        </div>

        {/* Referral Link */}
        <Card className="card-game-neon">
          <CardContent className="py-4 px-4 space-y-3">
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Your Referral Link</p>
            <div className="flex gap-2">
              <div className="flex-1 bg-black/60 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-cyan-400 font-mono truncate">
                {referralLink || "Loading..."}
              </div>
              <Button
                onClick={handleCopy}
                disabled={!referralCode}
                className="shrink-0 bg-purple-500 text-black hover:bg-purple-500 font-bold"
                size="sm"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Share this link. When someone signs up and creates a player account, you earn $10.00 virtual credit!
            </p>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="card-game-neon">
            <CardContent className="py-4 px-4 text-center">
              <Users className="w-5 h-5 mx-auto text-pink-400 mb-1" style={{ filter: 'drop-shadow(0 0 6px rgba(236, 72, 153, 0.6))' }} />
              <p className="text-2xl font-extrabold text-white" style={{ textShadow: '0 0 10px rgba(168, 85, 247, 0.5)' }}>{referrals.filter((r) => r.status === "converted").length}</p>
              <p className="text-xs text-purple-300/80">Friends Joined</p>
            </CardContent>
          </Card>
          <Card className="card-game-neon">
            <CardContent className="py-4 px-4 text-center">
              <DollarSign className="w-5 h-5 mx-auto text-cyan-400 mb-1" style={{ filter: 'drop-shadow(0 0 6px rgba(6, 182, 212, 0.6))' }} />
              <p className="text-2xl font-extrabold text-cyan-400" style={{ textShadow: '0 0 10px rgba(6, 182, 212, 0.6)' }}>${totalEarned.toFixed(2)}</p>
              <p className="text-xs text-purple-300/80">Total Earned</p>
            </CardContent>
          </Card>
        </div>

        {/* Referral History */}
        {referrals.length > 0 && (
          <Card className="card-game-neon">
            <CardContent className="py-4 px-4">
              <p className="text-sm font-bold text-white mb-3">Referral History</p>
              <div className="space-y-2">
                {referrals.map((ref) => (
                  <div key={ref.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <div>
                      <p className="text-sm text-white font-medium">
                        {(ref.referred as any)?.display_name || "Unknown"}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(ref.converted_at || ref.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className="text-sm font-bold text-cyan-400">+${Number(ref.credit_amount).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </GameLayout>
  );
}
